import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useStore } from "../../stores/store";

const baseUrl = import.meta.env.VITE_ROOT_URL;

const OMSTwitterContactAutocomplete = ({
  id = "",
  name = "",
  placeholder = "enter name",
  style = null,
  officeid = "",
  onChange,
  onlyAgents,
  clearOnSelect,
}) => {
  const { commonStore } = useStore();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = commonStore.token;
      const officeIdParam = officeid ? `&OfficeID=${officeid}` : "";

      // Load agents
      const agentResponse = await fetch(`${baseUrl}/contacts/searchAgentAjax?Query=${encodeURIComponent(inputValue)}${officeIdParam}&Limit=15`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });
      const agentData = await agentResponse.json();

      // Load contacts if not only agents
      let contactData = [];
      if (!onlyAgents) {
        const contactResponse = await fetch(`${baseUrl}/contacts/searchContactAjax?Query=${encodeURIComponent(inputValue)}${officeIdParam}&Limit=15`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        });
        contactData = await contactResponse.json();
      }

      const formattedSuggestions = [
        ...(agentData.length > 0
          ? [
              {
                group: "Agents",
                items: agentData.map((item) => ({
                  ...item,
                  displayText: item.displayName,
                  type: item.type,
                })),
              },
            ]
          : []),
        ...(contactData.length > 0
          ? [
              {
                group: "Contacts",
                items: contactData.map((item) => ({
                  ...item,
                  displayText: item.displayName,
                  type: item.type,
                })),
              },
            ]
          : []),
      ];

      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error loading options:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    loadOptions(value);
  };

  const handleSelect = (item) => {
    setInputValue(item.displayText);
    setShowDropdown(false);
    if (onChange) onChange(item);
    if (clearOnSelect) {
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      const firstItem = suggestions[0].items[0];
      if (firstItem) handleSelect(firstItem);
    }
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dropdown" style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        placeholder={placeholder}
        className="form-control"
        autoComplete="off"
      />
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "block",
          }}
        >
          {isLoading ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              Loading...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              No results found
            </div>
          ) : (
            suggestions.map((group) => (
              <div key={group.group}>
                <div className="dropdown-header" style={{ padding: "8px 12px", color: "#666", fontSize: "0.9em" }}>
                  {group.group}
                </div>
                {group.items.map((item) => (
                  <div
                    key={item.id}
                    className="dropdown-item"
                    onClick={() => handleSelect(item)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    <i className={`fa ${item.type === 1 ? "fa-user-circle" : "fa-user"} aria-hidden="true"`} style={{ marginRight: "8px" }}></i>
                    <span>{item.displayText}</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

OMSTwitterContactAutocomplete.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  style: PropTypes.object,
  officeid: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onlyAgents: PropTypes.bool,
  clearOnSelect: PropTypes.bool,
};

const OMSTwitterAutocomplete = ({ id = "", name = "", value = "", Country = "", style = null, onChange = undefined, onInfoChange = undefined }) => {
  const { commonStore } = useStore();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = commonStore.token;
      const response = await fetch(
        `${baseUrl}/settings/searchAreaAjax?Query=${encodeURIComponent(inputValue)}&Country=${encodeURIComponent(Country)}&Limit=15`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        }
      );
      const data = await response.json();

      const formattedSuggestions = data.map((item) => ({
        ...item,
        displayText: item.displayName,
      }));

      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error loading options:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    loadOptions(value);
  };

  const handleSelect = (item) => {
    setInputValue(item.displayText);
    setShowDropdown(false);
    if (onChange) onChange(item.displayText);
    if (onInfoChange) onInfoChange(item);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      handleSelect(suggestions[0]);
    }
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dropdown" style={{ position: "relative" }}>
      <input type="hidden" data-id={id} id="hdnAreaAutocompleteC" name="hdnAreaAutocompleteC" value={Country} />
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        className="form-control"
        autoComplete="off"
      />
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "block",
          }}
        >
          {isLoading ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              Loading areas...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              No areas found
            </div>
          ) : (
            suggestions.map((item) => {
              const arr = item.displayText.split(",");
              return (
                <div
                  key={item.id}
                  className="dropdown-item"
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <span>
                    <strong>{arr[0]}</strong>
                    <small>,{arr.slice(1).join(",")}</small>
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

OMSTwitterAutocomplete.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  Country: PropTypes.string,
  style: PropTypes.object,
  onChange: PropTypes.func,
  onInfoChange: PropTypes.func,
};

const OMSTwitterAutocomplete2 = ({ id = "", name = "", value = "", style = null, onSelect = undefined }) => {
  const { commonStore } = useStore();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = commonStore.token;
      const response = await fetch(`${baseUrl}/settings/searchAreaCityAjax?Query=${encodeURIComponent(inputValue)}&Limit=15`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });
      const data = await response.json();

      const formattedSuggestions = data.map((item) => ({
        ...item,
        displayText: item.suburbID ? `${item.town}, ${item.suburb}` : item.town,
      }));

      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error loading options:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    loadOptions(value);
  };

  const handleSelect = (item) => {
    setInputValue(item.displayText);
    setShowDropdown(false);
    if (onSelect) onSelect(item);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      handleSelect(suggestions[0]);
    }
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dropdown" style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        className="form-control"
        autoComplete="off"
      />
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "block",
          }}
        >
          {isLoading ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              Loading cities...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              No cities found
            </div>
          ) : (
            suggestions.map((item) => (
              <div
                key={item.id}
                className="dropdown-item"
                onClick={() => handleSelect(item)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                <span>{item.displayText}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

OMSTwitterAutocomplete2.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  style: PropTypes.object,
  onSelect: PropTypes.func,
};

OMSTwitterAutocomplete2.defaultProps = {
  id: "",
  name: "",
  value: "",
  style: null,
  onSelect: undefined,
};

const OMSTwitterAutocompleteAgent = ({ id = "", OfficeID = "", name = "", style = null, onChange = undefined }) => {
  const { commonStore } = useStore();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const token = commonStore.token;
      const response = await fetch(`${baseUrl}/contacts/searchAgentAjax?Func=Reassign&Query=${encodeURIComponent(inputValue)}&OfficeID=${OfficeID}&Limit=15`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });
      const data = await response.json();

      const formattedSuggestions = data.map((item) => ({
        ...item,
        displayText: item.displayName,
      }));

      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error loading options:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    loadOptions(value);
  };

  const handleSelect = (item) => {
    setInputValue(item.displayText);
    setShowDropdown(false);
    if (onChange) onChange(item);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      handleSelect(suggestions[0]);
    }
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dropdown" style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        className="form-control"
        autoComplete="off"
      />
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "block",
          }}
        >
          {isLoading ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              Loading agents...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              No agents found
            </div>
          ) : (
            suggestions.map((item) => (
              <div
                key={item.id}
                className="dropdown-item"
                onClick={() => handleSelect(item)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                <span>{item.displayText}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

OMSTwitterAutocompleteAgent.propTypes = {
  id: PropTypes.string,
  OfficeID: PropTypes.string,
  name: PropTypes.string,
  style: PropTypes.object,
  onChange: PropTypes.func,
};

OMSTwitterAutocompleteAgent.defaultProps = {
  id: "",
  name: "",
  value: "",
  style: null,
  onChange: undefined,
  OfficeID: "",
};

const OMSTwitterAutocompleteSearch = ({ id = "", name = "", style = null, onChange = undefined }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadOptions = async (inputValue) => {
    if (!inputValue) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/Home/SearchQueryCloudAjax?Query=${encodeURIComponent(inputValue)}&Limit=15`);
      const data = await response.json();

      const formattedSuggestions = data.map((item) => ({
        ...item,
        displayText: item.displayName,
      }));

      setSuggestions(formattedSuggestions);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error loading options:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    loadOptions(value);
  };

  const handleSelect = (item) => {
    setInputValue(item.displayText);
    setShowDropdown(false);
    if (onChange) onChange(item);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && suggestions.length > 0) {
      handleSelect(suggestions[0]);
    }
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className="dropdown search-text" style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        placeholder="Search..."
        className="search-input"
        autoComplete="off"
      />
      {showDropdown && (suggestions.length > 0 || isLoading) && (
        <div
          ref={dropdownRef}
          className="dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
            backgroundColor: "white",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            display: "block",
          }}
        >
          {isLoading ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              Loading...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="dropdown-item" style={{ padding: "8px 12px", color: "#666" }}>
              No results found
            </div>
          ) : (
            suggestions.map((item) => (
              <div
                key={item.id}
                className="dropdown-item"
                onClick={() => handleSelect(item)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                <span>{item.displayText}</span>
              </div>
            ))
          )}
        </div>
      )}
      <i className="fa fa-search" aria-hidden="true" />
    </div>
  );
};

OMSTwitterAutocompleteSearch.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  style: PropTypes.object,
  onChange: PropTypes.func,
};

OMSTwitterAutocompleteSearch.defaultProps = {
  id: "",
  name: "",
  value: "",
  style: null,
  onChange: undefined,
};

export { OMSTwitterContactAutocomplete, OMSTwitterAutocomplete, OMSTwitterAutocomplete2, OMSTwitterAutocompleteAgent, OMSTwitterAutocompleteSearch };
